import { applyMode, initTheme } from './theme';
import { initSearch } from './search';
import { initArticle } from './article';
import { initConsent, initConsentControls } from './consentBanner';

const MENU = '[data-hc-menu]';
const ITEM = '[data-hc-menu-item]';

const partsOf = menu => ({
  button: menu.querySelector('[data-hc-menu-button]'),
  panel: menu.querySelector('[data-hc-menu-panel]'),
});

export const closeMenu = (menu, { restoreFocus = false } = {}) => {
  const { button, panel } = partsOf(menu);
  if (panel.hidden) return;
  panel.hidden = true;
  button.setAttribute('aria-expanded', 'false');
  if (restoreFocus) button.focus();
};

const closeMenusExcept = menu => {
  document.querySelectorAll(MENU).forEach(other => {
    if (other !== menu) closeMenu(other);
  });
};

export const openMenu = menu => {
  closeMenusExcept(menu);
  const { button, panel } = partsOf(menu);
  panel.hidden = false;
  button.setAttribute('aria-expanded', 'true');
};

const focusItem = (menu, position) => {
  const items = [...menu.querySelectorAll(ITEM)];
  if (!items.length) return;
  const current = items.indexOf(document.activeElement);
  const targets = {
    first: 0,
    last: items.length - 1,
    next: current === -1 ? 0 : (current + 1) % items.length,
    previous:
      current === -1
        ? items.length - 1
        : (current - 1 + items.length) % items.length,
  };
  items[targets[position]].focus();
};

const KEY_POSITIONS = {
  ArrowDown: 'next',
  ArrowUp: 'previous',
  Home: 'first',
  End: 'last',
};

const skipToTarget = link => {
  const target = document.querySelector(link.getAttribute('href'));
  if (!target) return;
  target.focus({ preventScroll: true });
  target.scrollIntoView();
};

const onClick = event => {
  const skipLink = event.target.closest('[data-hc-skip-link]');
  if (skipLink) {
    event.preventDefault();
    skipToTarget(skipLink);
    return;
  }

  const menu = event.target.closest(MENU);
  closeMenusExcept(menu);
  if (!menu) return;

  if (event.target.closest('[data-hc-menu-button]')) {
    if (partsOf(menu).panel.hidden) openMenu(menu);
    else closeMenu(menu);
  } else if (event.target.closest(ITEM)) {
    closeMenu(menu, { restoreFocus: true });
  }
};

const onKeydown = event => {
  const menu = event.target.closest(MENU);
  if (!menu) return;
  const { panel } = partsOf(menu);

  if (event.key === 'Escape' && !panel.hidden) {
    event.preventDefault();
    closeMenu(menu, { restoreFocus: true });
    return;
  }
  const position = KEY_POSITIONS[event.key];
  if (
    !position ||
    (panel.hidden && !['ArrowDown', 'ArrowUp'].includes(event.key))
  )
    return;

  event.preventDefault();
  if (panel.hidden) openMenu(menu);
  focusItem(menu, position);
};

const onFocusout = event => {
  const menu = event.target.closest(MENU);
  if (menu && !menu.contains(event.relatedTarget)) closeMenu(menu);
};

export const initBrandPortal = () => {
  initTheme();
  initConsentControls();
  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('focusout', onFocusout);
  document.addEventListener('turbo:load', () => {
    applyMode();
    initSearch();
    initArticle();
    initConsent();
  });
};
